<!-- ============================================================================ -->
<!-- TASK LOT ROW — En-tête de lot + tâches groupées                            -->
<!-- ============================================================================ -->
<div
  class="task-lot-row"
  [class.task-lot-row--receiving]="isReceiving"
  [style.--lot-color]="color || 'var(--eva-brand-primary)'"
  cdkDropList
  [id]="dropListId"
  [cdkDropListDisabled]="!enableDragDrop"
  [cdkDropListData]="displayTasks"
  [cdkDropListConnectedTo]="connectedDropLists"
  [cdkDropListEnterPredicate]="onlyAcceptTasks"
  (cdkDropListEntered)="onDragEntered()"
  (cdkDropListExited)="onDragExited()"
  (cdkDropListDropped)="onDropOnLot($event)"
>
  <!-- En-tête du lot -->
  <div class="task-lot-row__header" (click)="toggleCollapse()">
    <!-- Chevron / drag handle — ⠿ au hover si drag actif -->
    <div class="task-lot-row__collapse-handle">
      <i class="pi task-lot-row__chevron" [class.pi-chevron-right]="collapsed" [class.pi-chevron-down]="!collapsed"></i>
      @if (enableDragDrop) {
        <ng-icon
          name="heroBars3"
          class="task-lot-row__drag-icon"
          cdkDragHandle
          (click)="$event.stopPropagation()"
        ></ng-icon>
      }
    </div>

    <!-- Titre + compteur de tâches -->
    <div class="task-lot-row__title-block">
      @if (editing) {
        <input
          #titleInput
          class="task-lot-row__title-input"
          [(ngModel)]="editTitle"
          (keydown)="onTitleKeyDown($event)"
          (blur)="stopEditing()"
          (click)="$event.stopPropagation()"
          aria-label="Nom du lot"
          maxlength="100"
        />
      } @else {
        <span class="task-lot-row__title" (dblclick)="startEditing($event)" evaTooltip="Double-cliquer pour renommer">
          {{ lot.title }}
        </span>
      }
      <span class="task-lot-row__task-count"> &middot; {{ tasks.length }} tâche{{ tasks.length > 1 ? "s" : "" }} </span>
      <button
        type="button"
        class="task-lot-row__add-task-btn"
        (click)="requestAddTask($event)"
        title="Ajouter une tâche"
      >
        <ng-icon name="heroPlus"></ng-icon> Tâche
      </button>
    </div>

    <span class="task-lot-row__spacer"></span>

    <!-- Tag élément lié commun -->
    @if (showObjectLink && getCommonObjectLink(); as link) {
      <eva-tag [preset]="getObjectLinkPreset(link)" size="small" />
    }

    <!-- Plage de dates -->
    @if (getLotDateRange(); as range) {
      <span class="task-lot-row__date-range">
        <ng-icon name="heroCalendar"></ng-icon>
        {{ formatShortDate(range.min) }} → {{ formatShortDate(range.max) }}
      </span>
    }

    <!-- Score done/total -->
    <span class="task-lot-row__score">{{ doneCount }}/{{ tasks.length }}</span>

    <!-- Barre de progression -->
    <div class="task-lot-row__progress">
      <div
        class="task-lot-row__progress-fill"
        [class.task-lot-row__progress-fill--done]="lotPercent === 100"
        [style.width.%]="lotPercent"
      ></div>
    </div>

    <!-- Pourcentage -->
    <span class="task-lot-row__percent">{{ lotPercent }}%</span>

    <!-- Menu actions -->
    <div class="task-lot-row__menu" (click)="$event.stopPropagation()">
      <button type="button" class="task-lot-row__menu-btn" (click)="toggleLotMenu($event)">
        <ng-icon name="heroEllipsisVertical"></ng-icon>
      </button>
      @if (showLotMenu) {
        <div
          class="task-lot-row__menu-dropdown"
          [style.top.px]="lotMenuOpensUpward ? null : lotMenuPosition.top"
          [style.bottom.px]="lotMenuOpensUpward ? lotMenuPosition.bottom : null"
          [style.right.px]="lotMenuPosition.right"
        >
          <button type="button" class="task-lot-row__menu-item" (click)="openEditDialog($event)">
            <ng-icon name="heroPencil"></ng-icon>
            Modifier le lot
          </button>
          <button type="button" class="task-lot-row__menu-item" (click)="requestAddTask($event)">
            <ng-icon name="heroPlus"></ng-icon>
            Ajouter une tâche
          </button>
          <button type="button" class="task-lot-row__menu-item" (click)="requestMarkAllDone($event)">
            <ng-icon name="heroCheck"></ng-icon>
            Marquer tout fait
          </button>
          <div class="task-lot-row__menu-divider"></div>
          <button
            type="button"
            class="task-lot-row__menu-item task-lot-row__menu-item--danger"
            (click)="deleteLot($event)"
          >
            <ng-icon name="heroTrash"></ng-icon>
            Supprimer le lot
          </button>
        </div>
      }
    </div>
  </div>

  <!-- Tâches du lot -->
  @if (!collapsed) {
    <div class="task-lot-row__tasks">
      @for (task of displayTasks; track task.task_id) {
        <div class="task-lot-row__task-item" cdkDrag [cdkDragData]="task" [cdkDragDisabled]="!enableDragDrop">
          @if (editingTaskId === task.task_id) {
            <app-task-inline-edit
              [task]="task"
              [employees]="employees"
              (taskUpdated)="taskEdited.emit($event)"
              (cancelled)="editCancelled.emit()"
              (deleted)="taskDeleted.emit()"
            />
          } @else {
            <div class="task-lot-row__task-row-wrapper">
              <app-task-row
                [task]="task"
                [employees]="employees"
                [compact]="compact"
                [showObjectLink]="showObjectLink"
                [showAssignees]="showAssignees"
                [showProgress]="true"
                [selected]="selectedTaskIds.has(task.task_id)"
                [selectionMode]="selectionMode"
                (statusChange)="taskStatusChange.emit($event)"
                (taskClick)="taskClick.emit($event)"
                (selectionToggle)="selectionToggle.emit($event)"
                (contextMenuRequested)="contextMenuRequested.emit($event)"
                (menuAction)="taskMenuAction.emit($event)"
              />
              <!-- Boutons réorganisation ▲/▼ — visibles au survol -->
              @if (enableDragDrop && displayTasks.length > 1) {
                <div class="task-lot-row__reorder-btns" (click)="$event.stopPropagation()">
                  <button
                    type="button"
                    class="task-lot-row__reorder-btn"
                    title="Monter"
                    [disabled]="displayTasks[0].task_id === task.task_id"
                    (click)="moveTaskInLot(task, -1)"
                  >
                    <ng-icon name="heroChevronUp"></ng-icon>
                  </button>
                  <button
                    type="button"
                    class="task-lot-row__reorder-btn"
                    title="Descendre"
                    [disabled]="displayTasks[displayTasks.length - 1].task_id === task.task_id"
                    (click)="moveTaskInLot(task, 1)"
                  >
                    <ng-icon name="heroChevronDown"></ng-icon>
                  </button>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Placeholder lot vide -->
      @if (tasks.length === 0) {
        <div class="task-lot-row__empty">
          <ng-icon name="heroPlus"></ng-icon>
          Aucune tâche — glissez-en une ici ou ajoutez-en une
        </div>
      }
    </div>
  }
</div>

<!-- Dialog : Appliquer les collaborateurs aux tâches — masqué temporairement -->
<!-- <eva-dialog [(visible)]="showApplyDialog" header="Affecter les collaborateurs au lot" type="form" size="sm">
  <div class="apply-dialog">
    <div class="apply-dialog__avatars">
      @for (a of pendingLotAssigneeItems; track a.employee_id) {
        <span
          class="apply-dialog__avatar"
          [style.background-color]="a.badge_color || '#BEBEBE'"
          [title]="a.name"
        >
          {{ a.initials || (a.name || '??').substring(0, 2).toUpperCase() }}
        </span>
      }
    </div>

    <p class="apply-dialog__message">
      Des tâches de <strong>{{ pendingLotTitle }}</strong> ont déjà des collaborateurs affectés.
      Comment appliquer cette affectation ?
    </p>

    <div class="apply-dialog__options">
      <button type="button" class="apply-dialog__option" (click)="applyCollaborators('merge')">
        <span class="apply-dialog__option-title">Compléter</span>
        <span class="apply-dialog__option-desc">Ajouter ces collaborateurs aux affectations existantes de chaque tâche</span>
      </button>
      <button type="button" class="apply-dialog__option" (click)="applyCollaborators('replace')">
        <span class="apply-dialog__option-title">Remplacer</span>
        <span class="apply-dialog__option-desc">Remplacer l'affectation de toutes les tâches du lot par celle-ci</span>
      </button>
    </div>
  </div>

  <div dialog-footer>
    <eva-button label="Annuler" variant="outlined" severity="secondary" (onClick)="cancelApplyDialog()" />
  </div>
</eva-dialog> -->

<!-- Dialog : Modifier le lot -->
<eva-dialog [(visible)]="showEditDialog" header="Modifier le lot" type="form" size="sm">
  <div class="lot-edit-form">
    <eva-input
      label="Nom du lot"
      placeholder="ex. Distribution hydraulique"
      [(value)]="editForm.title"
      [required]="true"
    />
    <!-- Collaborateurs du lot — masqué temporairement -->
    <!-- <eva-select
      label="Collaborateurs du lot"
      [options]="employeeOptions"
      [value]="editFormAssigneeIds"
      (valueChange)="onEditFormAssigneesChange($event)"
      [multiple]="true"
      [showAvatar]="true"
      avatarSize="xsmall"
      [searchable]="true"
      placeholder="Affecter des collaborateurs..."
    /> -->
  </div>
  <div dialog-footer>
    <eva-button
      icon="heroTrash"
      label="Supprimer"
      variant="outlined"
      severity="danger"
      (onClick)="deleteLotFromDialog($event)"
    />
    <span class="lot-edit-form__footer-spacer"></span>
    <eva-button label="Annuler" variant="outlined" severity="secondary" (onClick)="showEditDialog = false" />
    <eva-button
      icon="heroCheck"
      label="Enregistrer"
      severity="primary"
      [disabled]="!editForm.title.trim()"
      [loading]="editSaving"
      (onClick)="submitEdit()"
    />
  </div>
</eva-dialog>
